import React, { Fragment, useState } from 'react'
import { DropDown, TextField } from './textfield'
import { CheckBox } from './checkbox'
import { Icon } from './icon'
import { Text } from './text'
import './table.css'
import { FORMATS, TYPES } from '../domain/ContentType'

const isFunction = value => value && (Object.prototype.toString.call(value) === "[object Function]" || "function" === typeof value || value instanceof Function);

/**
 * DataTable
 */
export const DataTable = (props) => {

    const { columns = [], rows = [], onRowSelection, onSort, onCheckAll, editable, outlined, expanded = false } = props
    const [sortDir, setSortDir] = useState({})

    function multiSort(array, sortObject = {}) {

        const sortKeys = Object.keys(sortObject);

        if (!sortKeys.length) {
            return array;
        }

        const keySort = (a, b, direction) => {
            direction = direction !== null ? direction : 1;

            if (a === b) { // If the values are the same, do not switch positions.
                return 0;
            }

            // If b > a, multiply by -1 to get the reverse direction.
            return a > b ? direction : -1 * direction;
        };

        return array.sort((a, b) => {
            let sorted = 0;
            let index = 0;

            // Loop until sorted (-1 or 1) or until the sort keys have been processed.
            while (sorted === 0 && index < sortKeys.length) {
                const key = sortKeys[index];
                if (key) {
                    const direction = sortObject[key];
                    sorted = keySort(a[key], b[key], direction);
                    index++;
                }
            }

            return sorted;
        });
    }

    function select(row, event) {
        if (event.target.id !== "checked") {
            if (onRowSelection) onRowSelection(row, event)
        }
    }

    function sort(dragged, dropped) {
        if (onSort) onSort(dragged, dropped)
    }

    function checkAll(id, value) {
        const ids = rows.map(row => row.id)
        if (onCheckAll) onCheckAll(ids, value)
    }

    const style = outlined ? "outlined" : ""
    return (
        <div className={`datatable8 ${style}`}>
            <table>
                <thead>
                    <tr>
                        {columns.map(({ id, label, type, item, sortable }) => {
                            const sort = sortDir[id] ? sortDir[id] : null
                            const [rowspan, colspan] = type === TYPES.ENTITY ? [1, Object.values(item).filter(v=>v.column===true).length] : [2, 1]
                            return (
                                <th key={id} rowSpan={rowspan} colSpan={colspan}>
                                    {id === "checked" ? <CheckBox onChange={checkAll} /> : <Text key={`th_${id}`}>{label}</Text>}
                                    {sortable ? <Icon icon="arrow_up" size="small" clickable /> : null}
                                </th>
                            )
                        })}
                        <th rowSpan={2} colSpan={1}></th>
                    </tr>
                    <tr>
                        {
                            columns
                                .filter(({ type }) => type === TYPES.ENTITY)
                                .map(column => {
                                    const { item } = column
                                    const fields = item ? Object.values(item) : []
                                    return fields
                                        .filter(field => field.column === true)
                                        .map(field => <th>{field.label}</th>)
                                })
                        }
                    </tr>
                </thead>
                <tbody>
                    {
                        multiSort(rows, sortDir).map(row => (
                            <DataTableRow key={row.id} row={row} columns={columns} onSelect={select} onDrop={sort} editable={editable} expanded={expanded} />
                        ))
                    }
                </tbody>
            </table>
        </div>
    )
}

/**
 * DataTable Row
 */
const DataTableRow = (props) => {

    const { row, columns = [], onSelect, editable, expanded = false } = props
    const { className} = row
    const [isInfoOpen, toggleInfo] = useState(expanded)
    const infoIcon = isInfoOpen ? 'expand_more' : 'expand_less'

    return (
        <Fragment>
            <tr className={className} onClick={ev => onSelect(row, ev)}>
                {columns.map((column, index) => <DataTableCell key={`${column.id}_${index}`} row={row} column={column} cell={row[column.id]} editable={editable} />)}
                {row.info ? <td><Icon icon={infoIcon} clickable action={() => toggleInfo(!isInfoOpen)} /></td> : <td></td>}
            </tr>
            {row.info && isInfoOpen ? (
                <tr className="table-row-info">
                    <td colSpan={columns.length + 1}>
                        {isFunction(row.info) ? row.info() : row.info}
                    </td>
                </tr>
            ) : null}
        </Fragment>
    )
}

/**
 * DataTable Cell
 */
const DataTableCell = ({ row, column, cell, editable }) => {

    
    const render = (type) => {
        const { id, disabled = false, min, max, onChange, format, options, item } = column
       
        if (id === "checked") {
            return <CheckBox id={id} value={cell} onChange={(id, value) => onChange(row.id, id, value)} />
        } else if (editable && onChange) {
            switch (type) {
                case "ICON": return <Icon icon={cell} />
                case "BOOLEAN": return <CheckBox id={id} value={cell} onChange={(id, value) => onChange(row.id, id, value)} disabled={disabled} />
                case "Boolean": return <CheckBox id={id} value={cell} onChange={(id, value) => onChange(row.id, id, value)} disabled={disabled} />
                case "SELECTION": return <DropDown id={id} value={cell} placeholder="--Select--" options={options} onChange={(id, value) => onChange(row.id, id, value)} />
                case "CHECK": return <CheckBox id={id} value={cell} onChange={(id, value) => onChange(row.id, id, value)} disabled={row.disabled} />
                case "CHECKABLE": return cell && cell.value ? <CheckBox id={id} value={cell.checked || false} label={cell.value} onChange={(id, checked) => onChange(row.id, id, cell.value, checked, cell)} /> : ''
                case "TEXTFIELD": return <TextField id={id} value={cell} onChange={(id, value) => onChange(row.id, id, value)} />
                case "String": return <StringCellEditor id={id} value={cell} options={options} onChange={(id, value) => onChange(row.id, id, value)} />
                case "Number": return <TextField id={id} type="number" value={cell} min={min} max={max} onChange={(id, value) => onChange(row.id, id, value)} />
                default: return cell
            }
        } else {
            switch (type) {
                case "ICON": return <Icon icon={cell} />
                case "Boolean": return <BooleanCellViewer id={id} value={cell} />
                case "String": return <StringCellViewer id={id} value={cell} format={format} options={options} />
                default: return cell
            }
        }
    }

    return column.type === TYPES.ENTITY ? <EntityCellViewer id={column.id} item={column.item} value={cell} /> : (
        <td key={column.id} className={column.id}>{render(column.type)}</td>
    )
}

/**
 * Entity Cell Viewer
 * @param {*} param0 
 * @returns 
 */
const EntityCellViewer = ({ id, item, value }) => {
    console.log(id, item, value)
    const fields = Object.values(item).filter(field => field.column === true)
    return fields.map( field => {
        return (<td key={field.id} className={`entity-cell ${field.id}`} >{value[field.id]}</td>)
    })
}

/**
 * Boolean Cell Viewer
 */
const BooleanCellViewer = ({ id, value = false }) => {
    const icon = value === true ? "check_box" : "check_box_outline_blank"
    return <Icon icon={icon} />
}

/**
 * String Cell Viewer
 */
const StringCellViewer = ({ id, value, format, options }) => {

    function buildOptions() {
        const opts = typeof options === 'function' ? options() : options
        return opts
      }
      
    const option = options ? buildOptions().find(o => o.value === value) : null
    let text = option ? option.label : value
    const locale = window.navigator.userLanguage || window.navigator.language;
    switch (format) {
        case FORMATS.URL:
            text = <a href={text} target="download" download >{text}</a>
            break;
        case FORMATS.IMG: 
            text = <img src={text} />
            break;
        case FORMATS.DATE: 
            let fecha = new Date(text)
            fecha.setMinutes(fecha.getMinutes() + fecha.getTimezoneOffset() + 1)
            text = fecha.toLocaleString( locale, { year: 'numeric', month: 'numeric', day: 'numeric'});
            break;
        case FORMATS.TIME: 
            text = new Date(text).toLocaleString( locale, { year: 'hour', month: 'minute', day: 'second' }); 
            break;
    }
    console.log(value, format)
    return (<div className="field-editor string-viewer">{text}</div>)
}

/**
 * String Cell Editor
 */
export const StringCellEditor = ({ id, value = '', options, onChange }) => {

    function change(id, value) {
        if (onChange) onChange(id, value)
    }

    return (
        <div className='field-editor string-editor'>
            {options ?
                <DropDown outlined id={id} value={value} onChange={change} options={options} />
                : <TextField outlined id={id} value={value} onChange={change} />
            }
        </div>
    )
}
